import { useCurrentBoxId } from "@/modules/core/store/useCurrentSpaceManagerStore"
import { type PriceObjectDto } from "@modules/common/priceObject/gateway/priceObjectApiDto"
import { useServicesContext } from "@modules/core/services/services.context"
import { StripeOnOffProgramSubscriptionEntity } from "@modules/programs/entities/stripeOnOffProgramSubscriptionEntity"
import { StripeProgramSubscriptionEntity } from "@modules/programs/entities/stripeProgramSubscriptionEntity"
import { UserProgramDetailsEntity } from "@modules/programs/entities/userProgramDetailsEntity"
import { UserProgramItemEntity } from "@modules/programs/entities/userProgramItemEntity"
import { type UserProgramDetailsDto } from "@modules/programs/gateway/programDto"
import {
  type CreateUserProgramInputDto,
  type UpdateUserProgramInputDto,
} from "@modules/programs/gateway/programInputDto"
import { ProgramFilter, ProgramType } from "@modules/programs/types/programTypes"
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query"

export const useProgram = (programId: string) => {
  const { programApi } = useServicesContext()
  const boxId = useCurrentBoxId()

  const { data, isLoading } = useSuspenseQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["program", programId],
    queryFn: async () => {
      return boxId ? await programApi.getProgramForBox(programId) : await programApi.getProgram(programId)
    },
  })

  return {
    data: UserProgramDetailsEntity.fromDto(data),
    isLoading,
  }
}

export const useProgramAsync = (programId?: string) => {
  const { programApi } = useServicesContext()
  const { data, isLoading } = useQuery({
    queryKey: ["program", programId],
    queryFn: async () => {
      return await programApi.getProgram(programId!)
    },
    enabled: !!programId,
  })
  return {
    data: data ? UserProgramDetailsEntity.fromDto(data) : undefined,
    isLoading,
  }
}

export const usePrograms = (options: {
  programFilter: ProgramFilter
  onlyWithPrice?: boolean
  page?: number
  size?: number
  sort?: "SUBSCRIPTION_DATE" | "CREATION_DATE"
}) => {
  const { programApi } = useServicesContext()

  const { data, isLoading, isFetching } = useSuspenseQuery({
    queryKey: ["programs", options.programFilter, options.page, options.size, options.sort, options.onlyWithPrice],
    queryFn: async () => {
      const order = options.sort === "CREATION_DATE" ? "CREATION_DATE" : "SUBSCRIPTION_DATE"
      const result = await programApi.getPrograms(
        options.programFilter,
        order,
        options.page,
        options.size,
        options.onlyWithPrice,
      )
      return {
        ...result,
        content: result.content.map((program) => UserProgramItemEntity.fromDto(program)),
      }
    },
  })

  return {
    data,
    isLoading,
    isFetching,
  }
}

export const useProgramCreate = () => {
  const { programApi } = useServicesContext()
  const queryClient = useQueryClient()
  const {
    mutateAsync: createProgram,
    isPending: isCreatingProgram,
    error,
  } = useMutation({
    mutationFn: async (programInput: CreateUserProgramInputDto) => {
      const result = await programApi.createProgram(programInput)
      return result
    },
    onSuccess: (data: UserProgramDetailsDto) => {
      queryClient.setQueryData(["program", data.id], data)
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
  })

  return {
    createProgram,
    isCreatingProgram,
    error,
  }
}

export const useProgramUpdate = () => {
  const { programApi } = useServicesContext()
  const queryClient = useQueryClient()
  const {
    mutateAsync: updateProgram,
    isPending: isUpdatingProgram,
    error,
  } = useMutation({
    mutationFn: async ({ programId, programInput }: { programId: string; programInput: UpdateUserProgramInputDto }) => {
      const result = await programApi.updateProgram(programId, programInput)
      return result
    },
    onSuccess: (data: UserProgramDetailsDto) => {
      queryClient.setQueryData(["program", data.id], data)
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
  })

  return {
    updateProgram,
    isUpdatingProgram,
    error,
  }
}

export const useProgramDelete = () => {
  const { programApi } = useServicesContext()
  const queryClient = useQueryClient()

  const {
    mutateAsync: deleteProgram,
    isPending: isDeletingProgram,
    error,
  } = useMutation({
    mutationFn: async ({ programId }: { programId: string; onDeleted: () => void }) => {
      return await programApi.deleteProgram(programId)
    },
    onSuccess: async (_data: void, { programId, onDeleted }: { programId: string; onDeleted: () => void }) => {
      onDeleted() // Trick to navigate to the training page before the queries are invalidated

      // Cancel any in-flight requests for this program so they don't complete
      await queryClient.cancelQueries({ queryKey: ["program", programId] })
      await queryClient.invalidateQueries({ queryKey: ["programs"] })
      queryClient.removeQueries({ queryKey: ["program", programId] })
    },
  })

  return {
    deleteProgram,
    isDeletingProgram,
    error,
  }
}

export const useProgramStripeSubscriptionInfos = (programId?: string, programType?: ProgramType) => {
  const { programInfiniteApi, programOnOffApi, boxB2BProgramApi } = useServicesContext()
  const boxId = useCurrentBoxId()

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["programStripeSubscriptionInfos", programId, programType, boxId],
    queryFn: async () => {
      if (!programId) return null

      if (programType === ProgramType.ONOFF) {
        const result = await programOnOffApi.getProgramOnOffStripeSubscriptionInfos(programId)
        return result ? StripeOnOffProgramSubscriptionEntity.fromDto(programId, result) : null
      } else if (programType === ProgramType.PRO && boxId) {
        const result = await boxB2BProgramApi.getProgramProStripeSubscriptionInfos(programId)
        return result ? StripeProgramSubscriptionEntity.fromDto(programId, result) : null
      }

      const result = await programInfiniteApi.getProgramInfiniteStripeSubscriptionInfos(programId)
      return result ? StripeProgramSubscriptionEntity.fromDto(programId, result) : null
    },
    enabled: !!programId,
  })

  return { data, isLoading, error, refetch }
}

export const useProgramUpdatePrice = () => {
  const { programApi } = useServicesContext()
  const queryClient = useQueryClient()

  const {
    mutateAsync: updateProgramPrice,
    isPending: isUpdatingProgramPrice,
    error,
  } = useMutation({
    mutationFn: async ({
      programId,
      price,
      trialDuration,
    }: {
      programId: string
      price: PriceObjectDto
      trialDuration: number | null
    }) => {
      return await programApi.updateProgramPrice(programId, price, trialDuration)
    },
    onSuccess: (
      data: UserProgramDetailsDto,
      variables: { programId: string; price: PriceObjectDto; trialDuration: number | null },
    ) => {
      queryClient.setQueryData(["program", variables.programId], data)
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
  })

  return {
    updateProgramPrice,
    isUpdatingProgramPrice,
    error,
  }
}

export const useProgramRemovePrice = () => {
  const { programApi } = useServicesContext()
  const queryClient = useQueryClient()

  const {
    mutateAsync: removeProgramPrice,
    isPending: isRemovingProgramPrice,
    error,
  } = useMutation({
    mutationFn: async (programId: string) => {
      return await programApi.removeProgramPrice(programId)
    },
    onSuccess: (data: UserProgramDetailsDto, programId: string) => {
      queryClient.setQueryData(["program", programId], data)
      queryClient.invalidateQueries({ queryKey: ["programs"] })
    },
  })

  return {
    removeProgramPrice,
    isRemovingProgramPrice,
    error,
  }
}
